import { appContext } from '../../../../AppContext';
import { IbssComponent } from '../../../../Components/Core/BaseComponent/IbssComponent';
import { BuiltInType } from '@iconics/providers.fadx-platform-api-client-v2';
import { JSX } from 'react';
import { IOntologyNode, ISolutionParameter } from './EditSolutionParameters';
import { Switch } from '@serendie/ui';
import { IBuiltInType } from '../../../../Providers.StaticData/StaticDataProvider';
import { Button, ButtonBar, Card, Dialog, DialogContent, DialogFooter, GridContainer, GridItem, RightButtons, Select, TextField } from '@iconics/components';
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { MultilingualTextField } from "@iconics/components";
import { Validator } from '@iconics/core';

class EditSolutionParameterDialog extends IbssComponent<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            valueTypes: [],
            id: '',
            enabled: false,
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            selectedValueType: undefined,
            selectedOntologyNodeId: '',
            ontologyNodeError: '',
            valueTypeError: '',
            concurrencyToken: '',
        };
    }

    public componentDidMount(): void
    {
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('ontologyNodeId', () => this.state.selectedOntologyNodeId, i => this.setState({ ontologyNodeError: i }))
            .isRequired();
        this.validator
            .addInput('valueType', () => this.state.selectedValueType ?? '', i => this.setState({ valueTypeError: i }))
            .isRequired();
        this.setState({ valueTypes: appContext().staticDataProvider.getBuiltInTypes() });
    }

    public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>): void
    {
        if (!prevProps.open && this.props.open)
        {
            this.handleOpen();
        }
    }

    private handleOpen(): void
    {
        const { selectedSolutionParameter } = this.props;

        this.setState({
            id: selectedSolutionParameter?.id ?? '',
            name: selectedSolutionParameter?.name ?? '',
            displayName: selectedSolutionParameter?.displayName ?? this.languageValuesHelper.default(),
            enabled: selectedSolutionParameter?.isEnabled === 'Yes' ? true : false,
            selectedOntologyNodeId: selectedSolutionParameter?.ontologyNodeId ?? '',
            selectedValueType: selectedSolutionParameter?.valueType ?? undefined,
            concurrencyToken: selectedSolutionParameter?.concurrencyStamp ?? '',
        });
    }

    private handleCancel(): void
    {
        this.props.onClose();
    }

    private handleSave(): void
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        if (this.props.selectedSolutionParameter == null)
        {
            this.create()
        }
        else
        {
            this.update()
        }
    }

    private async create(): Promise<void>
    {
        try
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.parameters.post({
                id: this.props.solutionId,
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    ValueType: this.state.selectedValueType,
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    OntologyNode_ID: this.state.selectedOntologyNodeId
                }
            });
            this.props.onSave();

        }
        catch (error)
        {
            console.error(error)
        }
    }

    private async update(): Promise<void>
    {
        try
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.solutions.byId.parameters.byParameterId.patch({
                parameterId: this.state.id,
                id: this.props.solutionId,
                tenancy: appContext().tenantId,
                body: {
                    Name: this.state.name,
                    DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                    ValueType: this.state.selectedValueType,
                    OntologyNode_ID: this.state.selectedOntologyNodeId,
                    IsEnabled: this.state.enabled ? 'Yes' : 'No',
                    '@odata.etag': this.state.concurrencyToken
                }
            });
            this.props.onSave();
        }
        catch (error)
        {
            console.error(error);
        }
    }

    public render(): JSX.Element
    {
        return (
            <Dialog
                isOpen={this.props.open}
                onOpenChange={() => this.handleCancel()}
            >
                <DialogContent>

                    <Card
                        title={this.labels.Platform_AssetWorX_EditNodeSolutions_HierarchyParameterTitle}
                        subTitle={this.labels.Platform_AssetWorX_EditNodeSolutions_HierarchyParameterSubTitle}
                    >
                        <GridContainer>
                            <GridItem>
                                <Select
                                    label={this.labels.HierarchyParameter_OntologyNode_Name}
                                    placeholder={this.labels.HierarchyParameter_OntologyNode_Name}
                                    value={this.state.selectedOntologyNodeId ? [this.state.selectedOntologyNodeId] : []}
                                    onSelect={e => this.setState({ selectedOntologyNodeId: e.value }, () => this.validator.validate('ontologyNodeId'))}
                                    items={this.props.ontologyNodes.map(x => ({ label: x.name, value: x.id }))}
                                    required
                                    invalid={!!this.state.ontologyNodeError}
                                    invalidMessage={this.state.ontologyNodeError}
                                />
                            </GridItem>
                            <GridItem>
                                <TextField
                                    label={this.labels.HierarchyParameter_Name}
                                    placeholder={this.labels.HierarchyParameter_Name}
                                    value={this.state.name}
                                    onChange={(e) => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                    required
                                    invalid={!!this.state.nameError}
                                    invalidMessage={this.state.nameError}
                                
                                />
                            </GridItem>
                            <GridItem>
                                <MultilingualTextField
                                    label={this.labels.HierarchyParameter_DisplayName}
                                    placeholder={this.labels.HierarchyParameter_DisplayName}
                                    value={this.state.displayName}
                                    onChange={(value) => this.setState({ displayName: value })}
                                    localisation={appContext().localisation}
                                />
                            </GridItem>
                            <GridItem>
                                <Select
                                    label={this.labels.HierarchyParameter_ValueType}
                                    placeholder={this.labels.HierarchyParameter_ValueType}
                                    value={this.state.selectedValueType ? [this.state.selectedValueType] : []}
                                    onSelect={e => this.setState({ selectedValueType: e.value as BuiltInType }, () => this.validator.validate('valueType'))}
                                    items={this.state.valueTypes.map(x => ({ label: x.name, value: x.id }))}
                                    required
                                    invalid={!!this.state.valueTypeError}
                                    invalidMessage={this.state.valueTypeError}
                                />
                            </GridItem>
                            <GridItem>
                                <Switch
                                    label={this.labels.HierarchyParameter_IsEnabled}
                                    checked={this.state.enabled}
                                    onCheckedChange={e => this.setState({ enabled: e.checked as boolean })}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </DialogContent>
                <DialogFooter>
                    <ButtonBar>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.handleCancel()}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                onClick={() => this.handleSave()}
                                disabled={!this.validator.isValid}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </DialogFooter>
            </Dialog>
        )
    }
}

export default EditSolutionParameterDialog;

export interface IProps
{
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    selectedSolutionParameter: ISolutionParameter | null;
    ontologyNodes: IOntologyNode[];
    solutionId: string;
}

export interface IState
{
    valueTypes: IBuiltInType[];
    id: string;
    enabled: boolean;
    name: string,
    nameError: string;
    displayName: ILanguageValues,
    selectedValueType?: BuiltInType,
    selectedOntologyNodeId: string,
    ontologyNodeError: string,
    valueTypeError: string,
    concurrencyToken: string,
}